import React, { useState } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import TeamSetup from './components/Auth/TeamSetup';
import Dashboard from './components/Dashboard/Dashboard';
import AdminDashboard from './components/Admin/AdminDashboard';
import AssignQuestions from './components/Admin/AssignQuestions';
import TeamReportDetail from './components/Admin/TeamReportDetail';
import TeamManagement from './components/Team/TeamManagement';
import TeamFeedbackPage from './components/Team/Feedback/TeamFeedbackPage';
import Evaluation from './components/Evaluation/Evaluation';
import Handbook from './components/Handbook/Handbook';
import RecommendationDetail from './components/Handbook/RecommendationDetail';
import Profile from './components/User/Profile';
import ReleaseNotes from './components/ReleaseNotes/ReleaseNotes';
import Feedback from './components/Feedback/Feedback';
import Network from './components/Network/Network';
import PublicRecommendations from './components/Public/PublicRecommendations';
import PublicElements from './components/Public/PublicElements';
import PublicConnections from './components/Public/PublicConnections';
import LandingPage from './components/Public/LandingPage';
import Header from './components/Layout/Header';
import Navigation from './components/Layout/Navigation';
import OnboardingTour from './components/Onboarding/OnboardingTour';
import './App.css';

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);

  return isLogin ? (
    <Login onSwitchToRegister={() => setIsLogin(false)} />
  ) : (
    <Register onSwitchToLogin={() => setIsLogin(true)} />
  );
};

const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { isAuthenticated, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const AppLayout = ({ children }) => {
  const { user } = useAuth();

  if (!user?.hasTeam) {
    return <TeamSetup />;
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Navigation />
        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="w-[80%] mx-auto h-full p-6">
            <OnboardingTour />
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

const AppRoutes = () => {
  const { isAuthenticated, user } = useAuth();

  // Public Routes that don't need auth
  const isPublicRoute = window.location.hash.startsWith('#/public');

  if (isPublicRoute) {
    return (
      <Routes>
        <Route path="/public/recommendations" element={<PublicRecommendations />} />
        <Route path="/public/elements" element={<PublicElements />} />
        <Route path="/public/connections" element={<PublicConnections />} />
        <Route path="/public/landing" element={<LandingPage />} />
         {/* Catch all other public routes or redirect */}
         <Route path="*" element={<Navigate to="/public/landing" replace />} />
      </Routes>
    );
  }

  if (!isAuthenticated) {
    return (
       <Routes>
          <Route path="/public/recommendations" element={<PublicRecommendations />} />
          <Route path="/public/elements" element={<PublicElements />} />
          <Route path="/public/connections" element={<PublicConnections />} />
          <Route path="/public/landing" element={<LandingPage />} />
          <Route path="*" element={<AuthPage />} />
       </Routes>
    );
  }

  if (!user?.hasTeam) {
    return <TeamSetup />;
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      {/* Public Routes also accessible when logged in */}
      <Route path="/public/recommendations" element={<PublicRecommendations />} />
      <Route path="/public/elements" element={<PublicElements />} />
      <Route path="/public/connections" element={<PublicConnections />} />
      <Route path="/public/landing" element={<LandingPage />} />

      <Route path="/dashboard" element={
        <ProtectedRoute>
          <AppLayout>
            <Dashboard />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/network" element={
        <ProtectedRoute>
          <AppLayout>
            <Network />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/evaluations" element={
        <ProtectedRoute>
          <AppLayout>
            <Evaluation />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/handbook" element={
        <ProtectedRoute>
          <AppLayout>
            <Handbook />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/handbook/recommendation/:id" element={
        <ProtectedRoute>
          <AppLayout>
            <RecommendationDetail />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/team-feedback" element={
        <ProtectedRoute>
          <AppLayout>
            <TeamFeedbackPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/team-management" element={
        <ProtectedRoute>
          <AppLayout>
            <TeamManagement />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <AppLayout>
            <Profile />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/release-notes" element={
        <ProtectedRoute>
          <AppLayout>
            <ReleaseNotes />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/feedback" element={
        <ProtectedRoute>
          <AppLayout>
            <Feedback />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute requireAdmin={true}>
          <AppLayout>
            <AdminDashboard />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/recommendations/:id/questions" element={
        <ProtectedRoute requireAdmin={true}>
          <AppLayout>
            <AssignQuestions />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/reports/team/:teamId" element={
        <ProtectedRoute requireAdmin={true}>
          <AppLayout>
            <TeamReportDetail />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/team-setup" element={<TeamSetup />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;